import React, { Component } from 'react';
import i18next from 'i18next';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import amp from 'lib/amp';
import {
  url as urlPropType,
  widget as widgetPropType,
  presentation as presentationPropType,
} from 'lib/CustomPropTypes';
import { decodeString } from 'lib/stringUtils';

import * as widgetTypes from './widgetTypes';

import styles from './styles.module.scss';

const WIDGET_SIZES = {
  SMALL: 'Small',
};

const hasMenuEmbed = (properties) => {
  const decodedHtml = decodeString(get(properties, '[\'embed-code\']', '')) || '';
  return !!decodedHtml.match(/class=["“”]menu-embed["“”]/);
};

const mapStateToProps = ({ article, shared, router }) => ({
  url: article.content && article.content[0] && article.content[0].url,
  vertical: shared.vertical,
  domain: router.domain,
});

@connect(mapStateToProps)
@amp(({ widget, url, domain }) => {
  const {
    fallbackUrl,
    mobileBaseline,
    name,
    properties,
  } = widget;

  const getWidget = () => {
    switch (name) {
      case 'youtubeplus':
        return (
          <amp-youtube
            data-videoid={properties['youtube-id']}
            width="480"
            height="270"
            layout="responsive"
          />
        );
      case 'tweetplus_embed': {
        const twitterRegex = /https?:\/\/twitter\.com\/(?:#!\/)?(?:\w+)\/status(?:es)?\/(\d+)/g;
        const match = twitterRegex.exec(fallbackUrl);
        if (match === null) {
          return null;
        }
        const id = match[1];
        return (
          <amp-twitter
            data-tweetid={id}
            width="480"
            height="540"
            layout="responsive"
          />
        );
      }
      case 'facebookplus_embedded_post':
        return (
          <amp-facebook
            class="fb-post"
            data-href={properties['facebookplus-post-url'] || properties['post-url']}
            width="480"
            height="660"
            layout="responsive"
            data-align-center
          />
        );
      case 'facebookvideo':
      case 'facebookvideosmall':
        return (
          <amp-facebook
            class="fb-vid"
            data-embed-as="video"
            data-href={properties['post-url']}
            width="480"
            height="270"
            layout="responsive"
            data-align-center
          />
        );
      case 'instagramplus':
        return (
          <amp-instagram
            class="insta"
            data-shortcode={properties['instagram-id']}
            width="1"
            height="1"
            layout="responsive"
            data-captioned
          />
        );
      default:
        return null;
    }
  };

  if (name === 'advanced_embed') {
    /* eslint-disable react/jsx-indent */
    if (hasMenuEmbed(properties)) return null;

    return (
      <a
        href={url.primary}
        className={`${styles.ampViewButton} db b-solid mv6 pa6 tc w-100 f4 lh-none`}
      >
        <span className="ttu founders-cond">
          <span className="dib icon icon-external-link mr2" />
          { i18next.t('View this graphic on') }
          {' '}
          {domain}
        </span>
      </a>
    );
    /* eslint-enable react/jsx-indent */
  }

  if (name === 'IFRAMELY_EXTERNAL_EMBED') {
    // Iframely will return either an AMP tag for a supported social network or an iframe
    // through the mobileBaseline property in the Bento API response
    return (
      <section
        className={styles.embedIframelyWidget}
        /* eslint-disable-next-line react/no-danger */
        dangerouslySetInnerHTML={{ __html: mobileBaseline }}
      />
    );
  }

  return (
    <section className={styles.embedWidget}>
      {getWidget()}
    </section>
  );
})

export class EmbedWidget extends Component {
  static propTypes = {
    ...widgetPropType.isRequired,
    url: urlPropType.isRequired, // eslint-disable-line react/no-unused-prop-types
    presentation: presentationPropType.isRequired,
    domain: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    insertEndmark: PropTypes.bool,
  }

  static defaultProps = {
    insertEndmark: false,
  }

  render() {
    const {
      presentation: {
        size,
      },
      widget: {
        baseline: html,
        name,
        properties = {},
        publisher: {
          name: vertical,
        },
      } = {},
      insertEndmark,
    } = this.props;

    let getWidgetType = widgetTypes[name];

    if (!getWidgetType) {
      if (html && vertical === 'today') {
        getWidgetType = widgetTypes.todayLegacyWidget;
      } else {
        return null;
      }
    }

    const containerClasses = classNames('embedWidget', {
      [styles.smallWidget]: size === WIDGET_SIZES.SMALL,
      [styles.fbpost]: name === 'facebookplus_embedded_post',
      [styles.twittersmall]: name === 'tweet_embed' && size === WIDGET_SIZES.SMALL,
      [styles.twitter]: name === 'tweetplus_embed' && size !== WIDGET_SIZES.SMALL,
      [styles.youtube]: name === 'youtubeplus',
      [styles.instagSmall]: name === 'instagramplus' && size === WIDGET_SIZES.SMALL,
      [styles.instagram]: name === 'instagramplus' && size !== WIDGET_SIZES.SMALL,
      [styles.opinary]: name === 'opinary',
      [styles.vimeo]: name === 'vimeo',
      [styles.pinterest]: /pinterest/.test(name),
      'embedded-endmark': insertEndmark,
    }, [styles[size]]);

    let widgetContainsMenuEmbed = false;

    if (name === 'advanced_embed' && html && hasMenuEmbed(properties)) {
      widgetContainsMenuEmbed = true;
    }

    // menu embed widget shouldn't have normal widget classes
    const widgetClassName = widgetContainsMenuEmbed
      ? styles.menuEmbedWidget
      : containerClasses;

    return (
      <section className={widgetClassName}>
        {getWidgetType(this.props)}
      </section>
    );
  }
}
